import React, { memo, useEffect, useState, useRef } from 'react'
import ReactFlow, {
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  useReactFlow,
} from 'reactflow'
import { customReportingViewsService } from '../../services/customReportingViewsService'
import {
  Box,
  CircularProgress,
  Tooltip,
  Popper,
  Paper,
  ClickAwayListener,
  IconButton,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import Activity from '../../pages/Admin/Workflow/nodes/Activity'
import EndRejectedActivity from '../../pages/Admin/Workflow/nodes/EndRejectedActivity'
import EndAcceptedActivity from '../../pages/Admin/Workflow/nodes/EndAcceptedActivity'
import FloatingEdge from '../../pages/Admin/Workflow/components/FloatingEdge'
import CustomConnectionLine from '../../pages/Admin/Workflow/components/CustomConnectionLine'
import ZoomInOutlinedIcon from '@mui/icons-material/ZoomInOutlined'
import ZoomOutOutlinedIcon from '@mui/icons-material/ZoomOutOutlined'
import {
  getNodesFromActivities,
  getEdgesFromRoutes,
} from '../../pages/Admin/Workflow/utils/transformer'
import axios from 'axios'
import { useAuthContext } from '../../hooks/useAuthContext'
import 'reactflow/dist/style.css'
import '../../utility/constants.js'
import { ROUTE_TYPES } from '../../utility/constants.js'

const nodeTypes = {
  activity: Activity,
  startActivity: Activity,
  endRejectedActivity: EndRejectedActivity,
  endAcceptedActivity: EndAcceptedActivity,
}

const edgeTypes = {
  floating: FloatingEdge,
}

const defaultEdgeOptions = {
  style: { strokeWidth: 3, stroke: '#656d92' },
  type: 'smoothstep',
  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: '#656d92',
  },
}

const connectionLineStyle = {
  strokeWidth: 3,
  stroke: '#656d92',
}

const ZoomControls = () => {
  const { zoomIn, zoomOut } = useReactFlow()

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 16,
        right: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        zIndex: 1300,
      }}
    >
      <Tooltip title="Zoom In">
        <IconButton
          onClick={() => zoomIn()}
          sx={{
            backgroundColor: '#6c47e4',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#6c47e4',
              color: '#ffffff',
            },
          }}
        >
          <ZoomInOutlinedIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title="Zoom Out">
        <IconButton
          onClick={() => zoomOut()}
          sx={{
            backgroundColor: '#6c47e4',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#6c47e4',
              color: '#ffffff',
            },
          }}
        >
          <ZoomOutOutlinedIcon />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

const EMPTY_ARRAY = []

const ProcessFlowViewer = ({
  processTemplateId,
  processReferanceNum,
  currentProcessData = EMPTY_ARRAY,
  processTemplates,
  templateRoutes = EMPTY_ARRAY,
}) => {
  const [executedActivityIds, setExecutedActivityIds] = useState([])
  const [executedRouteIds, setExecutedRouteIds] = useState([])
  const [activityUserAssignments, setActivityUserAssignments] = useState({})
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])
  const [templateActivities, setTemplateActivities] = useState([])
  const [templateRoutes_state, setTemplateRoutes] = useState([])
  const [loading, setLoading] = useState(Boolean(processTemplateId))
  const [tooltipInfo, setTooltipInfo] = useState({
    open: false,
    content: null,
    anchorEl: null,
    position: { x: 0, y: 0 },
  })

  const { user } = useAuthContext()

  const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL

  const fallbackPropsRef = useRef({
    templateRoutes,
    processTemplates,
    currentProcessData,
  })

  useEffect(() => {
    fallbackPropsRef.current = {
      templateRoutes,
      processTemplates,
      currentProcessData,
    }
  }, [templateRoutes, processTemplates, currentProcessData])

  useEffect(() => {
    let isMounted = true

    const fetchActivityUserAssignments = async () => {
      if (!processReferanceNum || !processTemplateId || !user) return

      try {
        const result = await customReportingViewsService.getPreviewTimelineFlowProcesses(
          user,
          processReferanceNum,
          processTemplateId
        )

        if (result.success && isMounted) {
          setActivityUserAssignments(result.data)
          const execSet = []
          const routeSet = []
          const executed = result.executedPath || []

          executed.forEach((item) => {
            const fromId =
              item.from_activity_template_id ??
              item.fromActivityTemplateId ??
              item.from_id ??
              item.from
            const toId =
              item.to_activity_template_id ?? item.toActivityTemplateId ?? item.to_id ?? item.to
            const actId = item.activity_template_id ?? item.activityTemplateId
            const routeId = item.to_route_template_id ?? item.route_template_id

            if (fromId != null) execSet.push(String(fromId))
            if (toId != null) execSet.push(String(toId))
            if (actId != null) execSet.push(String(actId))
            if (routeId != null) routeSet.push(String(routeId))
          })

          setExecutedActivityIds(execSet)
          setExecutedRouteIds(routeSet)
        }
      } catch (error) {
        console.error('Error fetching activity user assignments:', error)
      }
    }

    fetchActivityUserAssignments()

    return () => {
      isMounted = false
    }
  }, [processReferanceNum, processTemplateId, user])

  useEffect(() => {
    let isMounted = true

    if (!processTemplateId || !user?.token) {
      return
    }

    const fetchGenericWorkflowTemplate = async () => {
      try {
        setLoading(true)

        const axiosConfig = {
          headers: { Authorization: `Bearer ${user.token}` },
        }

        let activitiesData = []
        let routesData = []

        try {
          const activitiesResponse = await axios.get(
            `${NODE_BASE_URL}/admin/workflow/activities/${processTemplateId}`,
            axiosConfig
          )
          activitiesData = activitiesResponse.data
        } catch {
          try {
            const activitiesResponse = await axios.get(
              `${NODE_BASE_URL}/admin/activity-template/${processTemplateId}`,
              axiosConfig
            )
            activitiesData = activitiesResponse.data
          } catch {
            try {
              const templateResponse = await axios.get(
                `${NODE_BASE_URL}/template/${processTemplateId}`,
                axiosConfig
              )
              activitiesData = templateResponse.data.activities || []
              routesData = templateResponse.data.routes || []
            } catch {
              console.log('All API attempts failed, using fallback')
              throw new Error('All API endpoints failed')
            }
          }
        }

        if (routesData.length === 0) {
          try {
            const routesResponse = await axios.get(
              `${NODE_BASE_URL}/admin/workflow/routes/${processTemplateId}`,
              axiosConfig
            )
            routesData = routesResponse.data
          } catch {
            console.log('Routes API failed, using templateRoutes prop')
            routesData = fallbackPropsRef.current.templateRoutes || []
          }
        }

        if (!isMounted) return
        setTemplateActivities(activitiesData || [])
        setTemplateRoutes(routesData || [])
      } catch (error) {
        if (!isMounted) return
        console.error('Error fetching generic workflow template:', error)
        const {
          processTemplates: fbTemplates,
          templateRoutes: fbRoutes,
          currentProcessData: fbCurrentData,
        } = fallbackPropsRef.current

        if (fbTemplates && fbTemplates.length > 0) {
          const currentTemplate = fbTemplates.find(
            (template) => template.id === processTemplateId
          )

          if (currentTemplate) {
            setTemplateActivities(currentTemplate.activities || [])
            setTemplateRoutes(currentTemplate.routes || fbRoutes || [])
            return
          }
        }

        // Fallback for current process data
        if (fbCurrentData && fbCurrentData.length > 0) {
          const fallbackActivities = fbCurrentData.map((process, index) => ({
            id: process.activity_template_id,
            activity_template_name: process.activity_template_name,
            activity_type: process.activity_type,
            is_start: index === 0,
            rank: index + 1,
            workflow_metadata: {
              position: { x: index * 300 + 50, y: 100 },
            },
          }))
          setTemplateActivities(fallbackActivities)
          setTemplateRoutes(fbRoutes || [])
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchGenericWorkflowTemplate()

    return () => {
      isMounted = false
    }
  }, [processTemplateId, user, NODE_BASE_URL])

  // Click handler
  const onEdgeClick = (event, edge) => {
    event.preventDefault()
    event.stopPropagation()

    const routeInfo = templateRoutes_state.find(
      (route) =>
        route.from_activity_template_id?.toString() === edge.source &&
        route.to_activity_template_id?.toString() === edge.target
    )

    const fromActivity = templateActivities.find(
      (activity) => activity.id?.toString() === edge.source
    )
    const toActivity = templateActivities.find(
      (activity) => activity.id?.toString() === edge.target
    )

    const tooltipContent = {
      from: fromActivity?.activity_template_name || 'Unknown Activity',
      to: toActivity?.activity_template_name || 'Unknown Activity',
      routeName: routeInfo?.route_template_name || 'Default Route',
      routeType: routeInfo?.route_type || ROUTE_TYPES.PROGRESS,
      edgeColor: edge.style.stroke,
      isDashboard: edge.style.strokeDasharray ? true : false,
    }

    setTooltipInfo({
      open: true,
      content: tooltipContent,
      position: { x: event.clientX, y: event.clientY },
    })
  }

  const handleCloseTooltip = () => {
    setTooltipInfo({
      open: false,
      content: null,
      position: { x: 0, y: 0 },
    })
  }

  useEffect(() => {
    if (templateActivities.length > 0) {
      const visibleActivities = templateActivities.filter(
        (activity) => !activity.is_hidden_in_process_flow_view
      )

      // Create bridge routes for hidden activities to maintain workflow continuity
      const createBridgeRoutes = (allActivities, allRoutes, visibleActivities) => {
        const visibleIds = new Set(visibleActivities.map((a) => a.id?.toString()))
        const hiddenActivities = allActivities.filter((a) => !visibleIds.has(a.id?.toString()))

        let bridgeRoutes = [...allRoutes]

        hiddenActivities.forEach((hiddenActivity) => {
          const hiddenId = hiddenActivity.id?.toString()

          // Find routes TO the hidden activity
          const routesToHidden = allRoutes.filter(
            (route) => route.to_activity_template_id?.toString() === hiddenId
          )

          // Find routes FROM the hidden activity
          const routesFromHidden = allRoutes.filter(
            (route) => route.from_activity_template_id?.toString() === hiddenId
          )

          // Create bridge routes connecting predecessors to successors
          routesToHidden.forEach((toRoute) => {
            routesFromHidden.forEach((fromRoute) => {
              // Only create bridge if both source and target are visible
              if (
                visibleIds.has(toRoute.from_activity_template_id?.toString()) &&
                visibleIds.has(fromRoute.to_activity_template_id?.toString())
              ) {
                const bridgeRoute = {
                  id: `bridge-${toRoute.from_activity_template_id}-${fromRoute.to_activity_template_id}`,
                  route_template_id: `bridge-${toRoute.from_activity_template_id}-${fromRoute.to_activity_template_id}`,
                  from_activity_template_id: toRoute.from_activity_template_id,
                  to_activity_template_id: fromRoute.to_activity_template_id,
                  route_template_name: toRoute.route_template_name || 'Bridge Route',
                  route_type: toRoute.route_type,
                  is_bridge_route: true,
                }
                bridgeRoutes.push(bridgeRoute)
              }
            })
          })

          // Remove routes involving hidden activities
          bridgeRoutes = bridgeRoutes.filter((route) => {
            const fromId = route.from_activity_template_id?.toString()
            const toId = route.to_activity_template_id?.toString()
            return fromId !== hiddenId && toId !== hiddenId
          })
        })

        return bridgeRoutes
      }

      // Get only latest versions
      const getLatestVersionData = (processData) => {
        const latestVersionMap = new Map()
        processData.forEach((process) => {
          const key = process.activity_template_id?.toString()
          const currentVersion = process.version || 1
          if (
            !latestVersionMap.has(key) ||
            (latestVersionMap.get(key).version || 1) < currentVersion
          ) {
            latestVersionMap.set(key, process)
          }
        })
        return Array.from(latestVersionMap.values())
      }

      const latestProcessData = getLatestVersionData(currentProcessData)

      // Create bridge routes for hidden activities
      const processedRoutes = createBridgeRoutes(
        templateActivities,
        templateRoutes_state,
        visibleActivities
      )

      // Create nodes from VISIBLE template activities only
      const transformedNodes = getNodesFromActivities(visibleActivities)

      const visibleActivityIds = visibleActivities.map((activity) => activity.id?.toString())
      const visibleRoutes = processedRoutes.filter((route) => {
        const fromActivityVisible = visibleActivityIds.includes(
          route.from_activity_template_id?.toString()
        )
        const toActivityVisible = visibleActivityIds.includes(
          route.to_activity_template_id?.toString()
        )
        return fromActivityVisible && toActivityVisible
      })

      const transformedEdges = getEdgesFromRoutes(visibleRoutes, visibleActivities)

      const currentTaskActivities = latestProcessData.filter(
        (process) => process.status === 'active' || process.status === 'open'
      )
      const currentActiveTemplateIds = currentTaskActivities.map((activity) =>
        activity.activity_template_id?.toString()
      )

      const completedTemplateIds = latestProcessData
        .filter((process) => process.status === 'closed')
        .map((process) => process.activity_template_id?.toString())

      // Apply highlighting and add user assignment data for UPCOMING activities only
      const highlightedNodes = transformedNodes.map((node) => {
        const isCurrentPosition = currentActiveTemplateIds.includes(node.id)
        const isCompleted = completedTemplateIds.includes(node.id)
        const isUpcoming = !isCurrentPosition && !isCompleted
        const isInExecutedPath = executedActivityIds.includes(node.id)
        const nodeProcessData = latestProcessData.find(
          (p) => p.activity_template_id?.toString() === node.id
        )
        const upcomingUserAssignment = isUpcoming ? activityUserAssignments[node.id] : null

        let nodeStyle = {
          ...node.style,
          visibility: 'visible',
        }

        if (isCurrentPosition) {
          // Current active position - Orange with pulse (same color as current paths)
          nodeStyle = {
            ...nodeStyle,
            border: '4px solid #f09536',
            boxShadow: '0 0 25px rgba(255, 152, 0, 0.8)',
            backgroundColor: '#fff3e0',
          }
        } else if (!isInExecutedPath) {
          // Activities NOT in executed path - fade them
          nodeStyle = {
            ...nodeStyle,
            backgroundColor: '#f5f5f5',
            opacity: 0.7,
          }
        }

        return {
          ...node,
          style: nodeStyle,
          data: {
            ...node.data,
            // Add task-specific data including modified_by and version (latest only)
            taskReference: processReferanceNum,
            isCurrentPosition,
            isCompleted,
            isUpcoming,
            isInExecutedPath,
            readOnly: true,
            hideBadges: false,
            taskProcessData: nodeProcessData,

            // PRESERVE EXISTING USER DATA for all activities
            modifiedBy: nodeProcessData?.modified_by,
            version: nodeProcessData?.version,
            modifiedAt: nodeProcessData?.modified_at,
            status: nodeProcessData?.status,
            first_name: nodeProcessData?.first_name,
            last_name: nodeProcessData?.last_name,
            created_at: nodeProcessData?.created_at,

            // ADD NEW UPCOMING USER ASSIGNMENT DATA (only for upcoming activities)
            upcomingUserAssignment: upcomingUserAssignment,
            showUpcomingUser: isUpcoming && upcomingUserAssignment,
          },
        }
      })

      const executionOrderMap = new Map()

      latestProcessData
        .sort((a, b) => (a.id ?? 0) - (b.id ?? 0)) // ensure correct order
        .forEach((process, index) => {
          executionOrderMap.set(process.activity_template_id?.toString(), index)
        })

      // Enhance edges to show complete workflow with specific path visibility
      const enhancedEdges = transformedEdges.map((edge) => {
        const sourceIsCurrentPosition = currentActiveTemplateIds.includes(edge.source)

        // Check if BOTH source and target are in executed path
        const sourceInExecutedPath = executedActivityIds.includes(edge.source)
        const targetInExecutedPath = executedActivityIds.includes(edge.target)

        // Only consider executed if BOTH activities are in the executed path
        const isExecutedByOrder = sourceInExecutedPath && targetInExecutedPath

        // Route based detection (when route exists)
        const isRouteExecuted =
          edge.route?.route_template_id &&
          executedRouteIds.includes(edge.route.route_template_id.toString())

        // Additional validation: Check if source was actually completed
        const sourceProcess = latestProcessData.find(
          (p) => p.activity_template_id?.toString() === edge.source
        )
        const sourceCompleted = sourceProcess && sourceProcess.status === 'closed'

        // FINAL executed edge decision - all conditions must be true
        const isEdgeExecuted = (isRouteExecuted || isExecutedByOrder) && sourceCompleted

        // Route info
        const routeInfo = templateRoutes_state.find(
          (route) =>
            route.from_activity_template_id?.toString() === edge.source &&
            route.to_activity_template_id?.toString() === edge.target
        )

        const isForwardPath =
          routeInfo?.route_type === ROUTE_TYPES.PROGRESS || !routeInfo?.route_type

        const isSendBackPath =
          routeInfo?.route_type === ROUTE_TYPES.REGRESS || routeInfo?.route_type === 'reject'

        let edgeStyle = { ...edge.style }
        let markerEnd = { ...edge.markerEnd }
        let showEdge = true
        let isClickable = false

        // Future Path
        edgeStyle.stroke = '#656d92'
        edgeStyle.opacity = 0.5
        edgeStyle.strokeWidth = 3
        markerEnd.color = '#656d92'

        // Executed Path
        if (isEdgeExecuted && isForwardPath) {
          edgeStyle.stroke = '#555557ff'
          edgeStyle.opacity = 1
          edgeStyle.strokeWidth = 4
          markerEnd.color = '#555557ff'
          isClickable = true
        }

        // Current Activity Outgoing Path
        if (sourceIsCurrentPosition) {
          edgeStyle.stroke = '#f09536'
          edgeStyle.strokeWidth = 6
          edgeStyle.opacity = 1
          markerEnd.color = '#f09536'
          edgeStyle.filter = 'none'
          isClickable = true
        }

        // Send Back Path
        if (isSendBackPath) {
          if (sourceIsCurrentPosition) {
            edgeStyle.stroke = '#f09536'
            edgeStyle.strokeWidth = 6
            edgeStyle.strokeDasharray = '10,5'
            edgeStyle.opacity = 1
            markerEnd.color = '#f09536'
            isClickable = true
          } else {
            showEdge = false
            isClickable = false
          }
        }

        if (isClickable) {
          edgeStyle.cursor = 'pointer'
        }

        return {
          ...edge,
          label: '',
          style: edgeStyle,
          markerEnd,
          hidden: !showEdge,
          data: {
            ...edge.data,
            isClickable,
            routeInfo,
            isEdgeExecuted,
          },
        }
      })
      setNodes(highlightedNodes)
      setEdges(enhancedEdges)
    }
  }, [
    templateActivities,
    templateRoutes_state,
    currentProcessData,
    processReferanceNum,
    activityUserAssignments,
    executedActivityIds,
    executedRouteIds,
    setNodes,
    setEdges,
  ])

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '70vh',
          width: '100%',
          backgroundColor: '#fff',
        }}
      >
        <CircularProgress size={40} />
      </Box>
    )
  }

  if (templateActivities.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '70vh',
          flexDirection: 'column',
        }}
      >
        <div>No workflow template found</div>
        <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
          Template: {processTemplateId} | Task: {processReferanceNum}
        </div>
      </Box>
    )
  }

  // Main component content
  return (
    <div style={{ width: '100%', height: '70vh', position: 'relative' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onEdgeClick={onEdgeClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        connectionLineComponent={CustomConnectionLine}
        connectionLineStyle={connectionLineStyle}
        proOptions={{ hideAttribution: true }}
        fitView
        fitViewOptions={{
          padding: 0.15,
          includeHiddenNodes: false,
        }}
        attributionPosition="top-right"
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={true}
        edgesFocusable={false}
        edgesUpdatable={false}
        panOnDrag={true}
        zoomOnScroll={true}
        zoomOnPinch={true}
        minZoom={0.1}
        maxZoom={2}
      >
        <Background />
        <ZoomControls />
      </ReactFlow>

      {/* Route Details Tooltip */}
      {tooltipInfo.open && tooltipInfo.content && (
        <ClickAwayListener onClickAway={handleCloseTooltip}>
          <Popper
            open={tooltipInfo.open}
            anchorEl={null}
            placement="top"
            style={{
              position: 'fixed',
              left: tooltipInfo.position.x,
              top: tooltipInfo.position.y - 10,
              zIndex: 1300,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <Paper
              elevation={8}
              sx={{
                padding: 2,
                minWidth: '250px',
                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                border: `2px solid ${tooltipInfo.content.edgeColor}`,
                borderRadius: 2,
                position: 'relative',
              }}
            >
              <IconButton
                size="small"
                onClick={handleCloseTooltip}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  padding: '2px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: '16px' }} />
              </IconButton>

              <Box
                sx={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  marginBottom: 1,
                  paddingRight: 3,
                }}
              >
                Vote Details
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, fontSize: '12px' }}>
                <Box>
                  <strong>From:</strong> {tooltipInfo.content.from}
                </Box>
                <Box>
                  <strong>To:</strong> {tooltipInfo.content.to}
                </Box>
                <Box>
                  <strong>Vote:</strong> {tooltipInfo.content.routeName}
                </Box>
              </Box>
            </Paper>
          </Popper>
        </ClickAwayListener>
      )}

      {/* CSS for animations */}
      <style>{`
        .react-flow__node .MuiIconButton-root,
        .react-flow__node button {
          display: none !important;
        }

        .react-flow__edge.clickable {
          cursor: pointer !important;
        }

        @keyframes pulse {
          0% {
            box-shadow: 0 0 20px rgba(255, 152, 0, 0.6);
          }
          50% {
            box-shadow: 0 0 30px rgba(255, 152, 0, 0.9);
          }
          100% {
            box-shadow: 0 0 20px rgba(255, 152, 0, 0.6);
          }
        }
      `}</style>
    </div>
  )
}

export default memo(ProcessFlowViewer)
